import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01 } from '@/core/math/scalar';
import { deltaV as rocketEquation, exhaustVelocity, g0 } from '@/core/units';
import { AeroResult, computeAerodynamics } from '@/physics/Aerodynamics';
import { RigidBody } from '@/physics/RigidBody';
import { CelestialBody } from '@/universe/CelestialBody';
import { ControlAllocator } from './ControlAllocator';
import { FieldAscentDrive, FieldDriveMode } from './FieldAscentDrive';
import { MassModel, MassProperties } from './MassModel';
import { PowerReport, PowerSystem } from './PowerSystem';
import { Engine, EngineEnvironment } from './Propulsion';
import { ShipDef } from './ShipDef';
import { ThermalSystem } from './ThermalSystem';

/**
 * Everything the vessel needs to know about where it is this step. Assembled
 * once per step by the game loop so the spacecraft never has to reach back into
 * the universe, the terrain streamer or the renderer.
 */
export interface FlightEnvironment {
  /** Simulation time, seconds. */
  t: number;
  /** Body whose inertial frame the vessel's state is expressed in. */
  body: CelestialBody;
  /** Gravitational acceleration in the vessel's frame, m/s^2. */
  gravity: Vec3;
  /** Unit vector from the body centre toward the vessel. */
  up: Vec3;
  /** Height of the terrain above the datum directly below the vessel, metres. */
  terrainHeight: number;
  /** Altitude above the terrain, metres. */
  altitude: number;
  /** Altitude above the reference datum, metres. */
  altitudeAboveDatum: number;
  /** Air density, kg/m^3. */
  density: number;
  /** Air pressure, Pa. */
  pressure: number;
  /** Air temperature, K. */
  temperature: number;
  /** Speed of sound, m/s. */
  speedOfSound: number;
  /** Velocity of the air mass in the vessel's frame, m/s. */
  airVelocity: Vec3;
  /** Stellar irradiance at the vessel, W/m^2. */
  irradiance: number;
  /** Vessel position expressed in the body-fixed (rotating) frame. */
  positionFixed: Vec3;
  /**
   * Terrain radius (datum + height) in the direction of a position given in the
   * vessel's frame. Used for per-leg landing gear contact.
   */
  sampleTerrainRadius: (positionInFrame: Vec3) => number;
  /** Gravitational acceleration at an arbitrary point in the vessel's frame. */
  gravityAt: (positionInFrame: Vec3, out: Vec3) => Vec3;
}

export interface ControlInputs {
  /** Body-axis rotation demand, -1..1. x = pitch, y = yaw, z = roll. */
  rotation: Vec3;
  /** Body-axis translation demand, -1..1. x = right, y = up, z = forward. */
  translation: Vec3;
  /** Main engine throttle, 0..1. */
  throttle: number;
  /** Whether the main engine group is armed. */
  mainEngineArmed: boolean;
  /** Whether the atmospheric engine group is armed. */
  atmosphericEnginesArmed: boolean;
  /** Whether the landing thruster group is armed. */
  landingThrustersArmed: boolean;
  /** Field drive mode requested by the pilot or flight computer. */
  fieldDriveMode: FieldDriveMode;
  /** Target upward acceleration for field-drive ascent mode, m/s^2. */
  fieldDriveAscentAcceleration: number;
}

export function defaultControlInputs(): ControlInputs {
  return {
    rotation: new Vec3(),
    translation: new Vec3(),
    throttle: 0,
    mainEngineArmed: false,
    atmosphericEnginesArmed: false,
    landingThrustersArmed: false,
    fieldDriveMode: 'off',
    fieldDriveAscentAcceleration: 0,
  };
}

export interface ContactState {
  /** Number of landing gear contacts currently touching. */
  contacts: number;
  /** True when the vessel is resting stably on a surface. */
  landed: boolean;
  /** Largest normal impulse this step, N s. Used for damage. */
  peakImpact: number;
}

export interface DamageState {
  hull: number;
  landingGear: number;
  wings: number;
  /** Per-engine damage keyed by engine id. */
  engines: Map<string, number>;
}

export interface VesselTelemetry {
  mass: MassProperties;
  aero: AeroResult | null;
  power: PowerReport | null;
  hullTemperature: number;
  thrustTotal: number;
  thrustToWeight: number;
  gForce: number;
  dynamicPressure: number;
  fieldDriveAcceleration: number;
  fieldDriveEfficiency: number;
  fieldDriveTemperature: number;
  fieldDrivePower: number;
  fieldDriveLimit: string | null;
  contact: ContactState;
  propellantFraction: number;
}

/**
 * A physically simulated spacecraft.
 *
 * Control inputs never touch position, orientation or velocity. They command
 * throttles, gimbal angles, control-surface deflections and reaction-wheel
 * torque; the integrator turns those into motion. That constraint is what makes
 * assisted flight, direct flight and the autopilot macros all the same
 * simulation with different sources for the same commands.
 */
export class Spacecraft {
  readonly def: ShipDef;
  readonly body = new RigidBody();
  /** Reference frame the rigid body's state is expressed in. */
  frameId: string;
  /** Body whose frame is currently in use. */
  referenceBody: CelestialBody;

  readonly engines: Engine[] = [];
  readonly enginesById = new Map<string, Engine>();
  readonly fieldDrive: FieldAscentDrive | null;
  readonly power: PowerSystem;
  readonly thermal: ThermalSystem;
  readonly allocator = new ControlAllocator();

  private readonly massModel: MassModel;
  readonly massProperties: MassProperties;

  /** Propellant load by tank id, kg. */
  readonly tanks = new Map<string, number>();
  /** Cargo mass carried, kg. */
  cargoMass = 0;
  /** Cargo centre offset in body coordinates, metres. */
  readonly cargoOffset = new Vec3();

  readonly controls = defaultControlInputs();
  /** Control-surface deflection demand in body axes, -1..1. */
  readonly controlSurfaces = new Vec3();
  /** Reaction-wheel torque demand in body axes, -1..1. */
  readonly reactionWheelDemand = new Vec3();

  readonly damage: DamageState = {
    hull: 0,
    landingGear: 0,
    wings: 0,
    engines: new Map(),
  };

  readonly contact: ContactState = { contacts: 0, landed: false, peakImpact: 0 };

  lastAero: AeroResult | null = null;
  lastPower: PowerReport | null = null;
  lastFieldDriveAccel = 0;
  lastFieldDriveLimit: string | null = null;
  lastThrustTotal = 0;
  lastGForce = 0;

  private allocatorDirtyTimer = 0;
  /** True when no gear was touching last step; used to detect touchdowns. */
  private wasAirborne = true;
  private readonly scratchForce = new Vec3();
  private readonly scratchDir = new Vec3();
  private readonly scratchArm = new Vec3();
  private readonly gravityScratch = new Vec3();

  constructor(def: ShipDef, referenceBody: CelestialBody) {
    this.def = def;
    this.referenceBody = referenceBody;
    this.frameId = referenceBody.inertialFrameId;

    for (const e of def.engines) {
      const engine = new Engine(e);
      this.engines.push(engine);
      this.enginesById.set(e.id, engine);
      this.damage.engines.set(e.id, 0);
    }

    this.fieldDrive = def.fieldDrive ? new FieldAscentDrive(def.fieldDrive) : null;
    this.power = new PowerSystem(def.powerPlants, def.batteries);
    this.thermal = new ThermalSystem(def);
    this.massModel = new MassModel(def);

    for (const t of def.tanks) this.tanks.set(t.id, t.capacity);

    this.massProperties = this.massModel.compute(this.tanks, 0, this.cargoOffset);
    this.body.mass = this.massProperties.total;
    this.body.inertia.copy(this.massProperties.inertia);
  }

  // ---- Engine groups -------------------------------------------------------

  get mainEngines(): Engine[] {
    return this.engines.filter(
      (e) => e.def.type === 'chemical' || e.def.type === 'nuclearThermal' || e.def.type === 'fusion' || e.def.type === 'ion',
    );
  }

  get atmosphericEngines(): Engine[] {
    return this.engines.filter((e) => e.def.type === 'turbine');
  }

  get landingThrusters(): Engine[] {
    return this.engines.filter((e) => e.def.type === 'landing');
  }

  get rcsThrusters(): Engine[] {
    return this.engines.filter((e) => e.def.type === 'rcs');
  }

  // ---- Propellant ----------------------------------------------------------

  fuelOfType(fuelType: string): number {
    let total = 0;
    for (const t of this.def.tanks) {
      if (t.fuelType === fuelType) total += this.tanks.get(t.id) ?? 0;
    }
    return total;
  }

  capacityOfType(fuelType: string): number {
    let total = 0;
    for (const t of this.def.tanks) {
      if (t.fuelType === fuelType) total += t.capacity;
    }
    return total;
  }

  /** Drains fuel proportionally from every tank of the type. Returns kg drawn. */
  consumeFuel(fuelType: string, kg: number): number {
    if (kg <= 0) return 0;
    const available = this.fuelOfType(fuelType);
    if (available <= 0) return 0;
    const draw = Math.min(kg, available);
    const scale = draw / available;
    for (const t of this.def.tanks) {
      if (t.fuelType !== fuelType) continue;
      const cur = this.tanks.get(t.id) ?? 0;
      this.tanks.set(t.id, cur - cur * scale);
    }
    return draw;
  }

  /**
   * Remaining delta-v for a given propellant type, using the rocket equation
   * with the best vacuum Isp among engines burning that propellant.
   */
  deltaVForFuel(fuelType: string): number {
    let bestIsp = 0;
    for (const e of this.engines) {
      if (e.def.fuelType !== fuelType || e.isRcs) continue;
      bestIsp = Math.max(bestIsp, e.def.ispVacuum);
    }
    if (bestIsp <= 0) return 0;
    const fuel = this.fuelOfType(fuelType);
    const total = this.massProperties.total;
    return rocketEquation(exhaustVelocity(bestIsp), total, total - fuel);
  }

  /** Total delta-v across all propellant types, staged best-Isp-last. */
  totalDeltaV(): number {
    const types = new Set(this.def.tanks.map((t) => t.fuelType));
    let total = 0;
    for (const type of types) {
      const dv = this.deltaVForFuel(type);
      if (Number.isFinite(dv)) total += dv;
    }
    return total;
  }

  // ---- Simulation ----------------------------------------------------------

  /**
   * Advances the vessel one fixed step.
   *
   * Order matters: mass properties first (everything downstream depends on
   * them), then engine throttle slew, then the power resolve that may brown out
   * the field drive, then force accumulation, then integration.
   */
  step(dt: number, env: FlightEnvironment): void {
    this.updateMassProperties();

    const engineEnv: EngineEnvironment = { pressure: env.pressure, density: env.density };

    this.allocatorDirtyTimer -= dt;
    if (this.allocatorDirtyTimer <= 0) {
      this.allocator.configure(this.rcsThrusters, this.massProperties.centreOfMass, engineEnv, this.def.size[2]);
      this.allocatorDirtyTimer = 0.25;
    }

    this.applyEngineGroupArming();
    for (const e of this.engines) e.update(dt);

    // --- Power ------------------------------------------------------------
    const generation = this.power.generation(env.irradiance, (fuelType) => this.fuelOfType(fuelType) > 0);
    for (const e of this.engines) {
      this.power.request(`engine:${e.id}`, e.powerDemand(), e.isRcs ? 8 : 6);
    }
    const wheelPower = this.reactionWheelDemand.length() * this.def.reactionWheelPower;
    this.power.request('reactionWheels', wheelPower, 7);
    this.power.request('lifeSupport', 12000, 10);
    this.power.request('avionics', 4500, 9);

    let fieldDrivePowerBudget = 0;
    if (this.fieldDrive && this.fieldDrive.isActive) {
      // Field drive is deliberately the lowest priority consumer: it browns out
      // before life support does.
      fieldDrivePowerBudget = Math.max(
        0,
        this.power.headroom(generation, dt) - 12000 - 4500 - wheelPower,
      );
    }

    // --- Force accumulation ------------------------------------------------
    this.body.clearAccumulators();

    const nonGravAccel = new Vec3();

    // Thrust.
    let thrustTotal = 0;
    const fuelDraw = new Map<string, number>();
    for (const e of this.engines) {
      if (e.throttle <= 0) continue;
      const mag = e.thrustMagnitude(engineEnv);
      if (mag <= 0) continue;
      const dirBody = e.thrustDirection(this.scratchDir);
      const dirWorld = this.body.orientation.rotate(dirBody, new Vec3());
      this.scratchForce.copy(dirWorld).mulScalar(mag);
      Vec3.subVectors(e.mountPosition, this.massProperties.centreOfMass, this.scratchArm);
      const armWorld = this.body.orientation.rotate(this.scratchArm, new Vec3());
      this.body.applyForceAtPoint(this.scratchForce, armWorld);
      thrustTotal += mag;

      const flow = e.massFlow(engineEnv);
      e.record(mag, flow);
      fuelDraw.set(e.def.fuelType, (fuelDraw.get(e.def.fuelType) ?? 0) + flow * dt);
    }
    this.lastThrustTotal = thrustTotal;

    // Reaction wheels: torque without propellant, limited by power and by a
    // saturation model that bleeds authority as they spin up.
    if (this.reactionWheelDemand.lengthSq() > 0) {
      const wheelTorqueBody = this.reactionWheelDemand.clone().mulScalar(this.def.reactionWheelTorque);
      this.body.applyBodyTorque(wheelTorqueBody);
    }

    // Aerodynamics.
    if (env.density > 0) {
      const relAir = Vec3.subVectors(this.body.velocity, env.airVelocity);
      const aero = computeAerodynamics(
        this.effectiveAero(),
        relAir,
        env.density,
        this.body.orientation,
        this.body.angularVelocity,
        env.speedOfSound,
        this.controlSurfaces,
      );
      this.body.applyForce(aero.force);
      this.body.applyTorque(aero.torque);
      this.lastAero = aero;
    } else {
      this.lastAero = null;
    }

    // Field-Assisted Ascent Drive.
    let fieldHeat = 0;
    if (this.fieldDrive) {
      const verticalSpeed = this.body.velocity.dot(env.up);
      const out = this.fieldDrive.update(
        {
          gravity: env.gravity,
          mass: this.massProperties.total,
          verticalSpeed,
          targetAscentAcceleration: this.controls.fieldDriveAscentAcceleration,
          powerAvailable: fieldDrivePowerBudget,
          ambientTemperature: env.temperature,
          coolingMultiplier: 1 + Math.min(6, env.density * 3),
        },
        dt,
      );
      if (out.acceleration.lengthSq() > 0) {
        // Applied through the centre of mass: the field couples to the whole
        // hull, so it produces no torque.
        this.body.applyForce(out.acceleration.clone().mulScalar(this.massProperties.total));
      }
      this.power.request('fieldDrive', out.powerUsed, 1);
      fieldHeat = out.heatGenerated;
      this.lastFieldDriveAccel = out.acceleration.length();
      this.lastFieldDriveLimit = out.limitReason;
    }

    // Landing gear contact.
    this.resolveGroundContact(env, dt);

    // Accumulated non-gravitational acceleration (what an accelerometer sees).
    nonGravAccel.copy(this.body.force).mulScalar(1 / this.massProperties.total);
    this.body.lastProperAcceleration.copy(nonGravAccel);
    this.lastGForce = nonGravAccel.length() / g0;

    // --- Integration -------------------------------------------------------
    // Velocity Verlet with gravity re-evaluated at the new position; the
    // non-gravitational force is held constant across the step.
    const a0 = Vec3.addVectors(nonGravAccel, env.gravity);
    this.body.velocity.addScaled(a0, 0.5 * dt);
    this.body.position.addScaled(this.body.velocity, dt);
    const g1 = env.gravityAt(this.body.position, this.gravityScratch);
    const a1 = Vec3.addVectors(nonGravAccel, g1);
    this.body.velocity.addScaled(a1, 0.5 * dt);
    this.body.lastAcceleration.copy(a1);

    if (this.body.linearDamping > 0) {
      // Resting damping settles the vessel onto its gear. It must converge on
      // the *surface* velocity, not on zero: damping toward zero in the
      // inertial frame would steadily brake a parked vessel out of co-rotation
      // with the planet underneath it.
      const surfaceVel = this.referenceBody.surfaceVelocityAt(this.body.position, new Vec3());
      const k = Math.exp(-this.body.linearDamping * dt);
      this.body.velocity.sub(surfaceVel).mulScalar(k).add(surfaceVel);
    }

    this.body.integrateRotation(dt);

    // Safety net. A vessel spun up beyond this is structurally gone; clamping
    // keeps the integrator finite so the failure is reported as damage rather
    // than as a NaN that silently corrupts every downstream system.
    const spinRate = this.body.angularVelocity.length();
    const spinLimit = 12;
    if (!Number.isFinite(spinRate) || spinRate > spinLimit) {
      if (Number.isFinite(spinRate)) {
        this.body.angularVelocity.mulScalar(spinLimit / spinRate);
      } else {
        this.body.angularVelocity.set(0, 0, 0);
      }
      this.damage.hull = clamp01(this.damage.hull + dt * 0.5);
      this.damage.wings = clamp01(this.damage.wings + dt * 1.2);
    }

    if (this.contact.landed) {
      // Likewise, a landed vessel should end up rotating *with* the planet
      // rather than being held still against it.
      const spin = this.referenceBody.spinAxis.clone().mulScalar(this.referenceBody.spinRate);
      const k = Math.exp(-3.0 * dt);
      this.body.angularVelocity.sub(spin).mulScalar(k).add(spin);
    }

    // --- Consumption and thermal ------------------------------------------
    for (const [fuelType, kg] of fuelDraw) this.consumeFuel(fuelType, kg);
    for (const [fuelType, kg] of this.power.fuelBurn(dt)) this.consumeFuel(fuelType, kg);

    const powerReport = this.power.resolve(generation, dt);
    this.lastPower = powerReport;
    for (const e of this.engines) {
      e.powerSatisfaction = powerReport.perConsumer.get(`engine:${e.id}`) ?? 1;
    }

    let internalHeat = fieldHeat;
    for (const e of this.engines) internalHeat += e.heatOutput();
    for (const p of this.def.powerPlants) {
      if (!p.solar && this.power.isPlantEnabled(p.id)) internalHeat += p.heatOutput;
    }

    const thermalReport = this.thermal.update(
      {
        aeroHeatFlux: this.lastAero?.heatFlux ?? 0,
        exposedArea: this.def.aero.frontalArea,
        internalHeat,
        irradiance: env.irradiance,
        ambientTemperature: env.temperature,
        ambientDensity: env.density,
      },
      dt,
    );

    this.applyStructuralDamage(thermalReport.overheating, dt);
  }

  // ---- Internals -----------------------------------------------------------

  private updateMassProperties(): void {
    this.massModel.compute(this.tanks, this.cargoMass, this.cargoOffset, this.massProperties);
    this.body.mass = this.massProperties.total;
    this.body.inertia.copy(this.massProperties.inertia);
  }

  /** Aerodynamic config adjusted for wing damage. */
  private effectiveAero() {
    const base = this.def.aero;
    if (this.damage.wings <= 0) return base;
    const health = 1 - clamp01(this.damage.wings);
    return {
      ...base,
      wingArea: base.wingArea * health,
      controlSurfaceArea: base.controlSurfaceArea * health,
      // A damaged wing also adds drag rather than just losing lift.
      dragCoefficientAxial: base.dragCoefficientAxial * (1 + this.damage.wings * 0.6),
    };
  }

  /** Applies engine group arming to the commanded throttles. */
  private applyEngineGroupArming(): void {
    const c = this.controls;
    for (const e of this.mainEngines) {
      e.commandedThrottle = c.mainEngineArmed ? clamp01(c.throttle) : 0;
    }
    for (const e of this.atmosphericEngines) {
      e.commandedThrottle = c.atmosphericEnginesArmed ? clamp01(c.throttle) : 0;
    }
    if (!c.landingThrustersArmed) {
      for (const e of this.landingThrusters) e.commandedThrottle = 0;
    }
    if (this.fieldDrive) this.fieldDrive.mode = c.fieldDriveMode;
  }

  /**
   * Landing gear as a spring-damper per leg, plus tangential friction.
   *
   * Because each leg applies its force at its own offset from the centre of
   * mass, a landing on a slope produces a real tipping moment, and a heavy
   * cargo bias makes the vessel settle unevenly. Nothing about this is scripted.
   */
  private resolveGroundContact(env: FlightEnvironment, dt: number): void {
    const gear = this.def.landingGear;
    this.contact.contacts = 0;
    this.contact.peakImpact = 0;
    if (gear.contacts.length === 0) return;

    // Cheap rejection: if the vessel is far above the terrain, no leg can touch.
    if (env.altitude > this.def.size[1] * 2 + 12) {
      this.contact.landed = false;
      // Must match the "not landed" branch below. Without this, a vessel that
      // leaves the ground in one jump rather than a gradual climb — a debug
      // teleport, a reference-body rebase — keeps whatever damping it had
      // while landed, and the integrator's resting-damping term (below) then
      // bleeds off its velocity toward local co-rotation speed every tick,
      // silently turning a circular orbit into a decaying one.
      this.body.linearDamping = 0;
      this.body.angularDamping = 0.02;
      return;
    }

    const health = 1 - clamp01(this.damage.landingGear);
    const stiffness = gear.stiffness * Math.max(0.25, health);
    const damping = gear.damping * Math.max(0.25, health);
    let touching = 0;

    for (const c of gear.contacts) {
      const localOffset = new Vec3(c[0], c[1], c[2]).sub(this.massProperties.centreOfMass);
      const armWorld = this.body.orientation.rotate(localOffset, new Vec3());
      const pointPos = Vec3.addVectors(this.body.position, armWorld);
      const groundRadius = env.sampleTerrainRadius(pointPos);
      const pointRadius = pointPos.length();
      const rawPenetration = groundRadius - pointRadius;
      if (rawPenetration <= 0) continue;

      // Clamp the penetration used for the spring force. A leg can only
      // compress so far; beyond that the correct model is a firm push-out, not
      // a spring storing megajoules. Without this clamp any situation that puts
      // the hull deep inside the terrain — a bad spawn, a terrain LOD change
      // under a parked vessel — launches it into orbit.
      const maxCompression = Math.max(1.5, this.def.size[1] * 0.5);
      const penetration = Math.min(rawPenetration, maxCompression);

      touching++;
      const normal = pointPos.clone().normalize();

      // Velocity of this contact point *relative to the ground it is touching*.
      //
      // Three terms: the vessel's own velocity, its rotation about its centre of
      // mass, and — critically — the velocity of the rotating surface at this
      // point. Omitting the third term means a vessel parked at Halcyon's
      // equator is treated as sliding at 460 m/s, and friction then drags it
      // across the planet and tears the gear off. The contact solver must work
      // in the surface frame, not the inertial one.
      const groundVelocity = this.referenceBody.surfaceVelocityAt(pointPos, new Vec3());
      const pointVel = Vec3.addVectors(this.body.velocity, Vec3.crossVectors(this.body.angularVelocity, armWorld)).sub(
        groundVelocity,
      );
      const normalSpeed = pointVel.dot(normal);

      const springForce = stiffness * penetration;
      const dampForce = normalSpeed < 0 ? -damping * normalSpeed : 0;
      const normalForce = Math.max(0, springForce + dampForce);

      this.scratchForce.copy(normal).mulScalar(normalForce);
      this.body.applyForceAtPoint(this.scratchForce, armWorld);

      // Friction opposing tangential slip, capped by the Coulomb limit.
      const tangential = pointVel.rejectFromUnit(normal);
      const slip = tangential.length();
      if (slip > 1e-4) {
        const maxFriction = normalForce * gear.friction;
        const frictionMag = Math.min(maxFriction, slip * this.massProperties.total * 4);
        this.body.applyForceAtPoint(tangential.mulScalar(-frictionMag / slip), armWorld);
      }

      const impact = Math.abs(Math.min(0, normalSpeed));
      if (impact > this.contact.peakImpact) this.contact.peakImpact = impact;
    }

    this.contact.contacts = touching;
    // "Landed" means at rest relative to the surface, not relative to the
    // planet's inertial frame — a parked vessel is still moving at hundreds of
    // metres per second in the latter.
    const surfaceRelativeSpeed = Vec3.subVectors(
      this.body.velocity,
      this.referenceBody.surfaceVelocityAt(this.body.position, new Vec3()),
    ).length();
    this.contact.landed =
      touching >= 3 && surfaceRelativeSpeed < 1.0 && this.body.angularVelocity.length() < 0.05;

    // Extra damping while resting stops the legs buzzing against the springs.
    this.body.linearDamping = this.contact.landed ? 3.0 : 0;
    this.body.angularDamping = this.contact.landed ? 3.0 : 0.02;

    // Hard-landing damage is applied once, on the touchdown transition, using
    // the impact speed at the moment of contact. Applying it every step while a
    // leg stays compressed would destroy the gear of any vessel that merely sat
    // still for long enough.
    const justTouchedDown = touching > 0 && this.wasAirborne;
    if (justTouchedDown && this.contact.peakImpact > gear.maxImpactSpeed) {
      const over = (this.contact.peakImpact - gear.maxImpactSpeed) / gear.maxImpactSpeed;
      this.damage.landingGear = clamp01(this.damage.landingGear + over * 0.35);
      if (over > 0.6) {
        this.damage.hull = clamp01(this.damage.hull + (over - 0.6) * 0.25);
      }
    }
    this.wasAirborne = touching === 0;
    void dt;
  }

  /** Structural, thermal and dynamic-pressure damage accumulation. */
  private applyStructuralDamage(overheating: boolean, dt: number): void {
    if (overheating) {
      const d = this.thermal.overheatDamage(dt);
      this.damage.hull = clamp01(this.damage.hull + d);
      // Wings and radiators fail before the pressure hull does.
      this.damage.wings = clamp01(this.damage.wings + d * 2.2);
    }

    if (this.lastGForce > this.def.structuralLimitG) {
      const over = (this.lastGForce - this.def.structuralLimitG) / this.def.structuralLimitG;
      this.damage.hull = clamp01(this.damage.hull + over * dt * 0.4);
    }

    const q = this.lastAero?.dynamicPressure ?? 0;
    if (q > this.def.maxDynamicPressure) {
      const over = (q - this.def.maxDynamicPressure) / this.def.maxDynamicPressure;
      this.damage.wings = clamp01(this.damage.wings + over * dt * 0.5);
      this.damage.hull = clamp01(this.damage.hull + over * dt * 0.12);
    }

    for (const e of this.engines) {
      const d = this.damage.engines.get(e.id) ?? 0;
      e.damage = d;
    }
  }

  // ---- State management ----------------------------------------------------

  /**
   * Re-expresses the vessel in a different reference frame. The caller supplies
   * the already-converted state; this method exists so the direct write is in
   * one auditable place.
   */
  rebaseTo(body: CelestialBody, position: Vec3, velocity: Vec3, orientation: Quat, angularVelocity: Vec3): void {
    this.referenceBody = body;
    this.frameId = body.inertialFrameId;
    this.body.setState(position, velocity, orientation, angularVelocity);
  }

  telemetry(): VesselTelemetry {
    const totalCapacity = this.def.tanks.reduce((s, t) => s + t.capacity, 0);
    return {
      mass: this.massProperties,
      aero: this.lastAero,
      power: this.lastPower,
      hullTemperature: this.thermal.temperature,
      thrustTotal: this.lastThrustTotal,
      thrustToWeight:
        this.massProperties.total > 0
          ? this.lastThrustTotal / (this.massProperties.total * Math.max(0.01, this.referenceBody.surfaceGravity))
          : 0,
      gForce: this.lastGForce,
      dynamicPressure: this.lastAero?.dynamicPressure ?? 0,
      fieldDriveAcceleration: this.lastFieldDriveAccel,
      fieldDriveEfficiency: this.fieldDrive?.lastEfficiency ?? 0,
      fieldDriveTemperature: this.fieldDrive?.temperature ?? 0,
      fieldDrivePower: this.fieldDrive?.lastPowerUsed ?? 0,
      fieldDriveLimit: this.lastFieldDriveLimit,
      contact: this.contact,
      propellantFraction: totalCapacity > 0 ? clamp(this.massProperties.fuel / totalCapacity, 0, 1) : 0,
    };
  }
}
